import React from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Chip,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import Autocomplete from '@mui/material/Autocomplete';
import cities from '../data/cities.json';
import Popper from '@mui/material/Popper';
import { SwapHoriz } from '@mui/icons-material';

// Custom Popper for wider dropdown
function CustomPopper(props) {
  return <Popper {...props} style={{ width: 450, zIndex: 1300 }} placement="bottom-start" />;
}

function FlightSegment({
  segment,
  index,
  isActive,
  flightSegments,
  updateFlightSegment,
  removeFlightSegment,
  setDatePopoverAnchor,
  setActiveSegmentId,
  formatDate
}) {

  // Helper function to group and sort cities for autocomplete
  const getGroupedCities = () => {
    // Group cities by metro area
    const metroGroups = {};
    const singleCities = [];

    cities.forEach(city => {
      if (city.isMetroOnly) {
        metroGroups[city.code] = {
          metro: city,
          airports: []
        };
      } else if (city.sub && city.parentMetro) {
        if (!metroGroups[city.parentMetro]) {
          metroGroups[city.parentMetro] = {
            metro: null,
            airports: []
          };
        }
        metroGroups[city.parentMetro].airports.push(city);
      } else {
        singleCities.push(city);
      }
    });

    // Create final grouped array
    const result = [];

    // Add metro groups (main city first, then airports)
    Object.values(metroGroups).forEach(group => {
      if (group.metro) {
        result.push(group.metro);
        group.airports.forEach(airport => {
          result.push(airport);
        });
      }
    });

    // Add single cities
    singleCities.forEach(city => {
      result.push(city);
    });

    return result;
  };

  const groupedCities = getGroupedCities();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center',  mb: 1 , gap:0.2 }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 3,
          overflow: 'visible',
          border: '1px solid #e8eaed',
          backgroundColor: isActive ? '#f8f9fa' : 'white',
          minHeight: 48,
          flex: 1,
          position: 'relative',
          transition: 'all 0.2s ease',
          boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
          '&:hover': {
            border: '1px solid #dadce0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          },
          px: 2,
          py: 1
        }}
      >
        {/* From/To with floating swap icon */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 2, minWidth: 0 }}>
          {/* From Field */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
              options={groupedCities}
              getOptionLabel={(option) => option ? (option.displayname || `${option.city}, ${option.country} ${option.code}`) : ''}
              renderOption={(props, option) => {
                if (!option) return null; // Early return if option is null
                const isSelected = segment.from === `${option.city} (${option.code})`;
                const isSubAirport = option.sub;

                return (
                  <li {...props} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: isSubAirport ? '8px 12px 8px 48px' : '12px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    backgroundColor: '#fff'
                  }}>
                    <span style={{
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {isSubAirport && (
                        <SubdirectoryArrowRightIcon
                          sx={{
                            fontSize: '28px',
                            fontWeight: 900,
                            color: '#333333'
                          }}
                        />
                      )}
                      <img
                        src="/614.png"
                        alt="airplane"
                        style={{
                          width: '24px',
                          height: '24px',
                          objectFit: 'contain'
                        }}
                      />
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: isSubAirport ? 400 : 600,
                        fontSize: '16px',
                        color: '#333'
                      }}>
                        {isSubAirport ? option.name : `${option.city}, ${option.region || option.country}, ${option.country}`} <span style={{ color: '#666', fontWeight: 400 }}>{option.code}</span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginTop: 2 }}>
                        {isSubAirport ? `${option.city}, ${option.region || option.country}, ${option.country}` : (option.isMetroOnly ? 'All airports' : option.name)}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateFlightSegment(segment.id, 'from', `${option.city} (${option.code})`);
                      }}
                      style={{
                        width: '16px',
                        height: '16px',
                        marginLeft: 12,
                        cursor: 'pointer',
                        accentColor: '#1976d2',
                        backgroundColor: isSelected ? '#1976d2' : '#f5f5f5',
                        border: isSelected ? '2px solid #1976d2' : '2px solid #ddd',
                        borderRadius: '2px'
                      }}
                    />
                  </li>
                );
              }}
              PopperComponent={CustomPopper}
              PaperComponent={({ children, ...props }) => {
                // Check if there are any filtered options to show search results
                const hasOptions = React.Children.count(children) > 0;
                const showDefault = !hasOptions;
                
                return (
                  <Paper
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                      mt: 2,
                      mb: 2,
                      fontSize: '16px',
                      width: 450
                    }}
                  >
                    {showDefault ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                          }}>
                            <span style={{ fontSize: '20px' }}>👤</span>
                          </Box>
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1976d2', fontSize: '16px' }}>
                              Sign In / Sign Up
                            </Typography>
                            <Typography sx={{ color: '#666', fontSize: '14px' }}>
                              Access your searches on any device
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      children
                    )}
                  </Paper>
                );
              }}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    px: 0,
                    py: 0,
                    fontSize: '16px',
                    borderBottom: '1px solid #f0f0f0',
                    '&[aria-selected="true"]': {
                      backgroundColor: '#f3f5f6',
                    },
                    '&:hover': {
                      backgroundColor: '#f3f5f6',
                    },
                  },
                  borderRadius: 3,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                }
              }}
              value={groupedCities.find(c => `${c.city} (${c.code})` === segment.from) || null}
              onChange={(_, newValue) => updateFlightSegment(segment.id, 'from', newValue ? `${newValue.city} (${newValue.code})` : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="From?"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: {
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#333',
                      px: 0.5,
                      py: 0.5,
                      width: '100%',
                      pr:"0px"
                    }
                  }}
                  sx={{ width: '100%', pr:"0px !important" }}
                />
              )}
              popupIcon={null}
              clearIcon={null}
              renderTags={(value, getTagProps) => (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, alignItems: 'center' }}>
                  {value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.code}
                      label={option?.code}
                      size="small"
                      clearIcon={null}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: "#333",
                        fontSize: "12px",
                        height: "24px",
                        marginRight: 0,
                        marginBottom: 0,
                        fontWeight: 500,
                        border: "none",
                        display: "inline-flex",
                        flexShrink: 0,
                        '& .MuiChip-deleteIcon': {
                          fontSize: '14px',
                          color: '#666'
                        },
                        '&:hover': {
                          backgroundColor: "#e0e0e0"
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
            />
          </Box>
          {/* Divider instead of swap icon in multi-city */}
          <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center', mx: 1 }} />
          {/* To Field */}
          <Box sx={{ flex: 1, minWidth: 0 , p:0 }}>
            <Autocomplete
              options={groupedCities}
              getOptionLabel={(option) => option ? (option.displayname || `${option.city}, ${option.country} ${option.code}`) : ''}
              renderOption={(props, option) => {
                if (!option) return null; // Early return if option is null
                const isSelected = segment.to === `${option.city} (${option.code})`;
                const isSubAirport = option.sub;

                return (
                  <li {...props} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: isSubAirport ? '8px 12px 8px 48px' : '12px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    backgroundColor: '#fff'
                  }}>
                    <span style={{
                      marginRight: 12,
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {isSubAirport && (
                        <SubdirectoryArrowRightIcon
                          sx={{
                            fontSize: '28px',
                            fontWeight: 900,
                            color: '#333333'
                          }}
                        />
                      )}
                      <img
                        src="/614.png"
                        alt="airplane"
                        style={{
                          width: '24px',
                          height: '24px',
                          objectFit: 'contain'
                        }}
                      />
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: isSubAirport ? 400 : 600,
                        fontSize: '16px',
                        color: '#333'
                      }}>
                        {isSubAirport ? option.name : `${option.city}, ${option.region || option.country}, ${option.country}`} <span style={{ color: '#666', fontWeight: 400 }}>{option.code}</span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginTop: 2 }}>
                        {isSubAirport ? `${option.city}, ${option.region || option.country}, ${option.country}` : (option.isMetroOnly ? 'All airports' : option.name)}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateFlightSegment(segment.id, 'to', `${option.city} (${option.code})`);
                      }}
                      style={{
                        width: '16px',
                        height: '16px',
                        marginLeft: 12,
                        cursor: 'pointer',
                        accentColor: '#1976d2',
                        backgroundColor: isSelected ? '#1976d2' : '#f5f5f5',
                        border: isSelected ? '2px solid #1976d2' : '2px solid #ddd',
                        borderRadius: '2px'
                      }}
                    />
                  </li>
                );
              }}
              PopperComponent={CustomPopper}
              PaperComponent={({ children, ...props }) => {
                // Check if there are any filtered options to show search results
                const hasOptions = React.Children.count(children) > 0;
                const showDefault = !hasOptions;
                
                return (
                  <Paper
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                      mt: 2,
                      mb: 2,
                      fontSize: '16px',
                      width: 450
                    }}
                  >
                    {showDefault ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                          }}>
                            <span style={{ fontSize: '20px' }}>👤</span>
                          </Box>
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography sx={{ fontWeight: 600, color: '#1976d2', fontSize: '16px' }}>
                              Sign In / Sign Up
                            </Typography>
                            <Typography sx={{ color: '#666', fontSize: '14px' }}>
                              Access your searches on any device
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      children
                    )}
                  </Paper>
                );
              }}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    px: 0,
                    py: 0,
                    fontSize: '16px',
                    borderBottom: '1px solid #f0f0f0',
                    '&[aria-selected="true"]': {
                      backgroundColor: '#f3f5f6',
                    },
                    '&:hover': {
                      backgroundColor: '#f3f5f6',
                    },
                  },
                  borderRadius: 3,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                }
              }}
              value={groupedCities.find(c => `${c.city} (${c.code})` === segment.to) ||   null}
              onChange={(_, newValue) => updateFlightSegment(segment.id, 'to', newValue ? `${newValue.city} (${newValue.code})` : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="To?"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: {
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#333',
                      px: 2,
                      py: 0.5
                    }
                  }}
                  sx={{ width: '100%' }}
                />
              )}
              popupIcon={null}
              clearIcon={null}
              renderTags={(value, getTagProps) => (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, alignItems: 'center' }}>
                  {value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.code}
                      label={option?.code}
                      size="small"
                      clearIcon={null}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: "#333",
                        fontSize: "12px",
                        marginRight: 0,
                        marginBottom: 0,
                        fontWeight: 500,
                        p:0,
                        border: "none",
                        display: "inline-flex",
                        flexShrink: 0,
                        '& .MuiChip-deleteIcon': {
                          fontSize: '14px',
                          color: '#666'
                        },
                        '&:hover': {
                          backgroundColor: "#e0e0e0"
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
            />
          </Box>
        </Box>
        {/* Date Field */}
        <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center', mx: 2 }} />
        <Box sx={{ flex: 1, px: 2, py: 1 }}>
          <Button
            onClick={(e) => {
              setDatePopoverAnchor(e.currentTarget);
              setActiveSegmentId(segment.id);
            }}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              color: '#333',
              fontWeight: 500,
              fontSize: '14px',
              textTransform: 'none',
              px: 0,
              py: 0
            }}
          >
            {segment.date ? formatDate(segment.date) : 'Departure'}
          </Button>
        </Box>
        {/* Cabin Class */}
        <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center', mx: 2 }} />
        <Box sx={{ flex: 1, px: 2, py: 1 }}>
          <Select
            value={segment.cabin || 'Economy'}
            onChange={(e) => updateFlightSegment(segment.id, 'cabin', e.target.value)}
            variant="standard"
            disableUnderline
            IconComponent={() => null}
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              width: '100%',
              background: 'none',
              border: 'none',
              boxShadow: 'none',
              minHeight: 44,
              pl: 0,
              pr: 0,
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                minHeight: 44,
                padding: 0,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 2,
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(60,60,60,0.12)',
                  width: '260px',
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  left: 0,
                  p: 0,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="Economy" sx={{ fontWeight: (segment.cabin || 'Economy') === 'Economy' ? 700 : 400 }}>Economy</MenuItem>
            <MenuItem value="Premium economy" sx={{ fontWeight: (segment.cabin || 'Economy') === 'Premium economy' ? 700 : 400 }}>Premium Economy</MenuItem>
            <MenuItem value="Business" sx={{ fontWeight: (segment.cabin || 'Economy') === 'Business' ? 700 : 400 }}>Business</MenuItem>
            <MenuItem value="First" sx={{ fontWeight: (segment.cabin || 'Economy') === 'First' ? 700 : 400 }}>First</MenuItem>
          </Select>
        </Box>
      </Paper>

      {/* Remove Button - Outside the Paper, or placeholder for first segment */}
      {index === 0 ? (
        // First segment: show placeholder to maintain alignment
        <Box sx={{ width: 40, height: 40 }} />
      ) : (
        // Other segments: show remove button
        <IconButton
          onClick={() => removeFlightSegment(segment.id)}
          sx={{
            color: '#333',
            padding:"6px",
            '&:hover': {
              color: '#333',
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <Close  fontWeight={700} sx={{color:"#333", fontSize:"28px", fontWeight:"700"}}/>
        </IconButton>
      )}
    </Box>
  );
}

export default FlightSegment; 